import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Loader2, Send, Sparkles } from "lucide-react";
import PublicHeader from "../components/PublicHeader";

const PLAN_LABELS = {
  starter: "Starter (€29/mese, fino a 50 utenti)",
  pro: "Pro (€79/mese, illimitato + report)",
  custom: "Enterprise (preventivo)",
};

export default function Contact() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan") || "";
  const headcountParam = searchParams.get("headcount") || "";

  const [form, setForm] = useState({
    name: "", email: "", office_name: "", location: "", role_hint: "", message: "",
    plan_interest: planParam,
    headcount: headcountParam ? Number(headcountParam) : 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Refresh when query params change (e.g. coming back from /pricing with different plan)
    setForm((f) => ({
      ...f,
      plan_interest: planParam || f.plan_interest,
      headcount: headcountParam ? Number(headcountParam) : f.headcount,
    }));
  }, [planParam, headcountParam]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact-requests", form);
      setDone(true);
      toast.success("Richiesta inviata!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grain">
      <PublicHeader />

      <main className="max-w-2xl mx-auto px-4 sm:px-8 py-12 sm:py-20 animate-fade-in">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-3">
          Vuoi portarlo nel tuo ufficio?
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight mb-4">
          Parlaci della tua sede.
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-10">
          Schiscetta funziona in qualsiasi ufficio dove ci sono colleghi che cucinano e altri che
          vogliono pranzare meglio. Raccontaci dove sei e ti rispondiamo per attivare la tua sede,
          configurare il bot Telegram dedicato e accompagnarti nell'onboarding del primo gruppo di colleghi.
        </p>

        {done ? (
          <div
            className="bg-primary/5 border border-primary/30 rounded-lg p-8 text-center"
            data-testid="contact-success"
          >
            <CheckCircle2 className="w-10 h-10 text-primary mx-auto mb-3" />
            <h2 className="font-display text-xl font-medium mb-2">Richiesta ricevuta</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Ti risponderemo all'indirizzo che ci hai indicato per attivare la sede.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline"
            >
              Torna alla home <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-card border border-border rounded-lg p-6 sm:p-8 space-y-5">
            {form.plan_interest && PLAN_LABELS[form.plan_interest] && (
              <div
                className="flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-md p-3 -mt-1"
                data-testid="contact-plan-banner"
              >
                <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <div className="text-sm flex-1">
                  <div className="font-medium">Hai scelto il piano <span className="text-primary">{PLAN_LABELS[form.plan_interest]}</span></div>
                  {form.headcount > 0 && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Stima per {form.headcount} dipendenti — confermeremo lo scenario in fase di onboarding
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, plan_interest: "" })}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="contact-plan-clear"
                >
                  Cambia
                </button>
              </div>
            )}
            <Field label="Nome e cognome" required>
              <input
                required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name" className="cinput"
              />
            </Field>
            <Field label="Email di lavoro" required>
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contact-email" className="cinput"
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Ufficio / Ente" required>
                <input
                  required value={form.office_name}
                  onChange={(e) => setForm({ ...form, office_name: e.target.value })}
                  data-testid="contact-office" placeholder="es. Procura di Bari" className="cinput"
                />
              </Field>
              <Field label="Città">
                <input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  data-testid="contact-location" placeholder="es. Bari" className="cinput"
                />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Il tuo ruolo">
                <input
                  value={form.role_hint}
                  onChange={(e) => setForm({ ...form, role_hint: e.target.value })}
                  data-testid="contact-role" placeholder="es. cancelliere, dirigente, IT" className="cinput"
                />
              </Field>
              <Field label="Quanti dipendenti circa">
                <input
                  type="number" min="0" max="5000"
                  value={form.headcount || ""}
                  onChange={(e) => setForm({ ...form, headcount: Number(e.target.value) })}
                  data-testid="contact-headcount" placeholder="es. 30" className="cinput"
                />
              </Field>
            </div>
            {!form.plan_interest && (
              <Field label="Piano di interesse">
                <select
                  value={form.plan_interest}
                  onChange={(e) => setForm({ ...form, plan_interest: e.target.value })}
                  data-testid="contact-plan-select"
                  className="cinput"
                >
                  <option value="">— Non so ancora —</option>
                  <option value="starter">Starter — €29/mese</option>
                  <option value="pro">Pro — €79/mese</option>
                  <option value="custom">Enterprise — preventivo</option>
                </select>
              </Field>
            )}
            <Field label="Messaggio (opzionale)">
              <textarea
                rows={4} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
                placeholder="Quanti siete? Già ci sono colleghi che cucinano e vendono pasti?"
                className="cinput resize-none"
              />
            </Field>
            <button
              type="submit" disabled={submitting} data-testid="contact-submit"
              className="w-full px-5 py-3 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] disabled:opacity-50 transition-all inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? "Invio…" : "Invia richiesta"}
            </button>
            <p className="text-xs text-muted-foreground text-center">
              Le richieste sono visibili solo al team di Schiscetta. Nessuna terza parte coinvolta.
            </p>
          </form>
        )}

        <style>{`
          .cinput {
            width: 100%;
            padding: 0.625rem 0.75rem;
            background: white;
            border: 1px solid hsl(var(--border));
            border-radius: 0.375rem;
            font-family: "IBM Plex Sans", sans-serif;
            font-size: 0.95rem;
          }
          .cinput:focus {
            outline: none;
            border-color: hsl(var(--primary));
            box-shadow: 0 0 0 3px hsla(120, 18%, 41%, 0.15);
          }
        `}</style>
      </main>

      <footer className="border-t border-border py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center text-xs text-muted-foreground">
          Schiscetta · piattaforma interna per uffici pubblici e privati · 2026
        </div>
      </footer>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">
        {label}{required && <span className="text-accent ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
